import React, { useState, useEffect, useRef } from 'react';
import {
  ActionsDiv,
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  BreadCrumble,
  Button,
  Card,
  CollapsibleHeader,
  FirstText,
  FormGrid,
  FormGroup,
  InfoLink,
  InfoName,
  InfoValue,
  InputText,
  Label,
  MoreInformation,
  ProductDiv,
  ProductShort,
  SecondText,
  Select,
  Separator,
  ShortLabel,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  Title,
} from './style';
import { ChevronDown, ChevronUp } from 'lucide-react';
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg';
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'; // Corrigi o caminho relativo
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg';
import AddProductForm from '../AddProductForm';
import { useOutletContext, useParams } from 'react-router-dom';
import axios from 'axios';

// Interfaces para os dados do produto e da API (revisadas para consistência)
interface FamilyData {
  id: number;
  name: string;
  groups?: GroupData[];
  colors?: { name: string }[];
}

interface GroupData {
  id: number;
  name: string;
  products?: any[];
}

interface ProductApiData {
  colorCount: number;
  discount: number | null;
  fashionOrderId?: number;
  fashionProduct: {
    id: number;
    name: string;
    price: number;
    size: string[];
    group: { id: number; name?: string; family?: { id: number; name?:string; collectionId?: number; } };
  };
  fashionProductId: number;
  sizeQuantities: { [key: string]: number };
  subtotal: number;
  totalItems: number;
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string;
  price?: number;
  colors?: string[];
  totalQuantity?: number;
  totalSubtotal?: number;
}


type FashionEditContent = {
  handleEditSave: React.MutableRefObject<() => void>;
};

type CompanyData = {
  id: number;
  companyName: string;
};

const EditFashionOrder = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false); // Controla a visibilidade do formulário de adição
  const { orderId } = useParams();
  const { handleEditSave } = useOutletContext<FashionEditContent>();
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([]); // Tipado com ProductApiData[]
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null); // Controla qual formulário de edição de produto está aberto

  const [formData, setFormData] = useState({
    supplierId: 0,
    userId: 0,
    companyId: 0,
    orderType: '',
    paymentCondition: '',
    typeOfShipping: '',
    trackingCode: '',
    attachment: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '',
    quantityPieces: '',
    totalCost: '',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  });

  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [suppliers, setSuppliers] = useState<CompanyData[]>([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axios.get(
          'https://companies-260311756054.southamerica-east1.run.app/companies?filters[0]=1&filters[1]=2'
        );

        const data = res.data;

        const clientes = data.filter((item: any) =>
          item.companyTypes?.includes('Cliente')
        );
        const fornecedores = data.filter((item: any) =>
          item.companyTypes?.includes('Fornecedor')
        );

        setCompanies(clientes);
        setSuppliers(fornecedores);
      } catch (err) {
        console.error('Erro ao buscar empresas:', err);
      }
    };

    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/fashion-order/${orderId}`);
        const data = response.data;
        console.log('Dados recebidos da API no EditFashionOrder:', data);
        setFormData({
          supplierId: Number(data.supplierId),
          userId: Number(data.userId),
          companyId: Number(data.companyId),
          orderType: data.orderType,
          paymentCondition: data.paymentCondition,
          typeOfShipping: data.typeOfShipping,
          trackingCode: data.trackingCode,
          attachment: data.attachment,
          finalDateOfDelivery: data.finalDateOfDelivery,
          startDateOfDelivery: data.startDateOfDelivery,
          extraInformation: data.extraInformation,
          quantityItems: data.quantityItems,
          quantityPieces: data.quantityPieces,
          totalCost: data.totalCost,
          deliveryTime: data.deliveryTime,
          shippingCost: data.shippingCost,
          maximumBillingAmount: data.maximumBillingAmount,
        });

        // GARANTIA: sempre defina addedProducts como um array
        if (data.products && Array.isArray(data.products)) {
          setAddedProducts(data.products);
        } else {
          setAddedProducts([]);
          console.warn('API retornou non-array para products, inicializando addedProducts como array vazio.');
        }
      } catch (error) {
        console.error('Erro ao buscar o pedido:', error);
        setAddedProducts([]); // Em caso de erro, também defina como array vazio
      }
    };

    fetchCompanies();
    fetchOrder();
  }, [orderId]);

  const saveRef = useRef<() => Promise<void> | void>(() => {});

  saveRef.current = async () => {
    try {
      const payload = {
        ...formData,
        finalDateOfDelivery: new Date(formData.finalDateOfDelivery).toISOString(),
        startDateOfDelivery: new Date(formData.startDateOfDelivery).toISOString(),
        totalCost: cleanCurrency(formData.totalCost),
        products: addedProducts.map(product => {
          // Garante que o payload do PUT reflita a estrutura ProductApiData
          return {
            fashionProductId: product.fashionProduct?.id || product.fashionProductId,
            discount: product.discount,
            colorCount: product.colorCount,
            sizeQuantities: product.sizeQuantities,
            totalItems: product.totalItems,
            subtotal: product.subtotal,
            // Certifique-se de que a família e o grupo são passados no formato esperado pelo backend
            // Com base na sua API de PUT, a estrutura aninhada dentro de fashionProduct.group é mais provável.
            fashionProduct: {
              ...(product.fashionProduct || {}), // Mantenha propriedades existentes
              id: product.fashionProduct?.id || product.fashionProductId,
              name: product.fashionProduct?.name || product.name,
              price: product.fashionProduct?.price || product.price,
              group: {
                ...(product.fashionProduct?.group || {}), // Mantenha propriedades existentes
                id: product.fashionProduct?.group?.id || product.group?.id,
                name: product.fashionProduct?.group?.name || product.group?.name,
                family: { // Family aninhada dentro do group
                    ...(product.fashionProduct?.group?.family || {}), // Mantenha propriedades existentes
                    id: product.fashionProduct?.group?.family?.id || product.family?.id,
                    name: product.fashionProduct?.group?.family?.name || product.family?.name,
                }
              }
            }
          };
        }),
      };

      console.log('Payload enviado para o PUT do Pedido:', payload);
      const response = await axios.put(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/fashion-order/${orderId}`, payload);
      console.log('Resposta do PUT do Pedido:', response.data);

      alert('Pedido atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar o pedido:', error);
      if (error.response) {
        console.error('Erro da API:', error.response.data);
      }
      alert('Erro ao atualizar o pedido');
    }
  };

  const cleanCurrency = (value: any): number => {
    const stringValue = String(value || '');
    const number = Number(stringValue.replace(/[^0-9,-]+/g, "").replace(',', '.'));
    return isNaN(number) ? 0 : number;
  };

  // useEffect para calcular e atualizar os valores dinamicamente no resumo principal do pedido
  useEffect(() => {
    let totalItemsCount = 0; // Contagem de produtos únicos
    let totalPiecesSum = 0; // Soma de todas as peças (totalItems de cada produto)
    let totalCostSum = 0; // Soma de todos os subtotais

    addedProducts.forEach(product => {
      totalItemsCount += 1; // Cada objeto no addedProducts é um "item"
      totalPiecesSum += Number(product.totalItems || 0);
      totalCostSum += product.subtotal || 0; // Soma dos subtotais de cada produto
    });

    setFormData(prevState => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: `R$ ${totalCostSum.toFixed(2).replace('.', ',')}`,
    }));
  }, [addedProducts, setFormData]); // Depende de addedProducts para recalcular

  const handleRemoveProduct = (indexToRemove: number) => {
    setAddedProducts(prev => prev.filter((_, i) => i !== indexToRemove));
  };

  useEffect(() => {
    handleEditSave.current = saveRef.current;
  }, [saveRef.current]);

  const formatDate = (isoDateString: string | null | undefined): string => {
    if (!isoDateString) {
      return '';
    }
    const date = new Date(isoDateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Função para atualizar um produto existente no array 'addedProducts'
  // Esta função é chamada pelo PedidoForm (AddProductForm) via onProductUpdated
  const handleUpdateProduct = (oldProduct: any, updatedProduct: any) => {
    console.log('EditFashionOrder - handleUpdateProduct (EDIT) chamado!');
    console.log('EditFashionOrder - oldProduct:', oldProduct);
    console.log('EditFashionOrder - updatedProduct recebido do PedidoForm:', updatedProduct);

    // Encontra o índice do produto original no array addedProducts
    const indexToUpdate = addedProducts.findIndex(p => p.fashionProductId === oldProduct.fashionProductId);

    if (indexToUpdate !== -1) {
      setAddedProducts(prevProducts => {
        const newProducts = [...prevProducts];
        newProducts[indexToUpdate] = updatedProduct; // Substitui o produto antigo pelo atualizado
        console.log('EditFashionOrder - addedProducts atualizado (edição):', newProducts);
        return newProducts;
      });
    } else {
      console.warn('EditFashionOrder - Produto a ser atualizado não encontrado no array addedProducts.');
    }
    setOpenProductIndex(null); // Fecha o formulário após a atualização
  };

  // NOVA FUNÇÃO: Para adicionar um NOVO produto ao array 'addedProducts'
  // Esta função é chamada pelo PedidoForm (AddProductForm) via onProductAdded
  const handleNewProductAdded = (newProduct: ProductApiData) => {
    console.log('EditFashionOrder - handleNewProductAdded (ADD) chamado!');
    console.log('EditFashionOrder - newProduct recebido do PedidoForm:', newProduct);
    setAddedProducts(prevProducts => [...prevProducts, newProduct]); // Adiciona o novo produto
    setShowAddProduct(false); // Fecha o formulário de adição
    setOpenProductIndex(null); // Garante que nenhum formulário de edição esteja aberto
    console.log('EditFashionOrder - addedProducts atualizado (adição):', [...addedProducts, newProduct]);
  };


  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Editar pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid>
          <FormGroup>
            <Label>Fornecedor</Label>
            <Select
              value={formData.supplierId}
              onChange={(e) => setFormData({ ...formData, supplierId: Number(e.target.value) })}
            >
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.companyName}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Vendedor</Label>
            <Select
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: Number(e.target.value) })}
            >
              {/* Você pode preencher opções dinamicamente aqui se tiver uma lista de usuários */}
              <option value={formData.userId}>{formData.userId}</option>
              <option value="2">Vendedor B</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Cliente</Label>
            <Select
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: Number(e.target.value) })}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.companyName}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>{isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formData.orderType}
                  onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                >
                  <option>{formData.orderType}</option>
                  {/* Adicione outras opções de tipo de pedido aqui */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <Select
                  value={formData.startDateOfDelivery}
                  onChange={(e) => setFormData({ ...formData, startDateOfDelivery: e.target.value })}
                >
                  <option>{formatDate(formData.startDateOfDelivery)}</option>
                  {/* Adicione outras opções de data ou use um input type="date" */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <Select
                  value={formData.finalDateOfDelivery}
                  onChange={(e) => setFormData({ ...formData, finalDateOfDelivery: e.target.value })}
                >
                  <option>{formatDate(formData.finalDateOfDelivery)}</option>
                  {/* Adicione outras opções de data ou use um input type="date" */}
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Condição de pagamento</Label>
                <Select
                  value={formData.paymentCondition}
                  onChange={(e) => setFormData({ ...formData, paymentCondition: e.target.value })}
                >
                  <option>{formData.paymentCondition}</option>
                  {/* Adicione outras opções de condição de pagamento */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Tipo de frete</Label>
                <Select
                  value={formData.typeOfShipping}
                  onChange={(e) => setFormData({ ...formData, typeOfShipping: e.target.value })}
                >
                  <option>{formData.typeOfShipping}</option>
                  {/* Adicione outras opções de tipo de frete */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <Select
                  value={formData.shippingCost}
                  onChange={(e) => setFormData({ ...formData, shippingCost: e.target.value })}
                >
                  <option>{formData.shippingCost}</option>
                  {/* Adicione outras opções de valor de frete ou use um input */}
                </Select>
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <Select
                  value={formData.trackingCode}
                  onChange={(e) => setFormData({ ...formData, trackingCode: e.target.value })}
                >
                  <option>{formData.trackingCode}</option>
                  {/* Adicione outras opções de código de rastreio ou use um input */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Anexos</Label>
                <Select
                  value={formData.attachment}
                  onChange={(e) => setFormData({ ...formData, attachment: e.target.value })}
                >
                  <option>{formData.attachment}</option>
                  {/* Adicione opções de anexos ou um input para upload */}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  value={formData.maximumBillingAmount}
                  onChange={(e) => setFormData({ ...formData, maximumBillingAmount: e.target.value })}
                />
              </FormGroup>
            </FormGrid>
            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type="text"
                  value={formData.extraInformation}
                  onChange={(e) => setFormData({ ...formData, extraInformation: e.target.value })}
                />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formData.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formData.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formData.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      <>
        {/* Mapeamento dos produtos adicionados para exibição e edição */}
        {Array.isArray(addedProducts) && addedProducts.length > 0 && (
          <>
            {addedProducts.map((product, index) => {
              console.log('Aqui é o produto no EditFashionOrder (summary):', product);
              // LÓGICA PARA OBTER OS NOMES DE FAMÍLIA E GRUPO DE FORMA CONSISTENTE
              let familyName = '-';
              // Baseado na estrutura: product.fashionProduct.group.family.name
              if (product.fashionProduct?.group?.family?.name) {
                familyName = product.fashionProduct.group.family.name;
              } else if (product.family?.name) { // Caso seja o objeto de produto adicionado localmente
                familyName = product.family.name;
              }


              let groupName = '-';
              // Baseado na estrutura: product.fashionProduct.group.name
              if (product.fashionProduct?.group?.name) {
                groupName = product.fashionProduct.group.name;
              } else if (product.group?.name) { // Caso seja o objeto de produto adicionado localmente
                groupName = product.group.name;
              }


              const productFields = [
                { label: 'Produto', value: product.fashionProduct?.name ?? '-', showIcon: true },
                { label: 'Episódio', value: familyName, showIcon: true },
                /* { label: 'Grupo', value: groupName, showIcon: true }, */
                { label: 'Precificação', value: (typeof product.fashionProduct?.price === 'number' ? `R$ ${product.fashionProduct.price.toFixed(2).replace('.', ',')}` : '-') , showIcon: true },
                { label: 'Cores', value: product.colorCount ?? '-', showIcon: true }, // Como não tem cores direto, use o colorCount
                { label: 'Quantidade de peças', value: product.totalItems ?? '-', showIcon: true },
                { label: 'Subtotal', value: (typeof product.subtotal === 'number' ? `R$ ${product.subtotal.toFixed(2).replace('.', ',')}` : '-') , showIcon: false },
              ];

              return (
                <Card key={product.fashionProductId || index}> {/* Use ID ou index para a key */}
                  <ProductShort>
                    <ProductDiv>
                      {productFields.map(({ label, value, showIcon }, i) => (
                        <ShortLabel key={i}>
                          <span style={{ fontWeight: 'normal' }}>{label}:</span> {value} {showIcon && <img style={{ marginLeft: '0.5rem'}} src={rectangle} alt="" />}
                        </ShortLabel>
                      ))}

                      <ActionsDiv className="flex items-center gap-2">
                        <img
                          src={trash}
                          alt="Remover"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveProduct(index)}
                        />
                        <Button
                          onClick={() => {
                            console.log('Produto ao expandir (EditFashionOrder):', product);
                            setOpenProductIndex(openProductIndex === index ? null : index);
                          }}
                        >
                          {openProductIndex === index ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </Button>

                      </ActionsDiv>
                    </ProductDiv>
                  </ProductShort>
                  {/* Renderiza o AddProductForm para edição se o índice corresponder */}
                  {openProductIndex === index && (
                    <AddProductForm
                      order={addedProducts} // Passa o array COMPLETO para o AddProductForm
                      setOrder={setAddedProducts} // Passa o setter para o AddProductForm
                      product={product} // Passa o produto ESPECÍFICO para edição
                      onClose={() => setOpenProductIndex(null)} // Fecha o formulário de edição
                      onProductUpdated={(oldProd, updatedProd) => handleUpdateProduct(oldProd, updatedProd)} // Passa a função de atualização, removi o index daqui
                      onRemoveProduct={handleRemoveProduct} // Passa a função de remoção
                      isEditing={true} // Indica que está em modo de edição
                      onProductAdded={handleNewProductAdded} // Passar a nova prop para adição
                    />
                  )}
                </Card>
              );
            })}
          </>
        )}
      </>


      <AddProductContainer>
        {/* Renderiza o AddProductForm para adicionar um NOVO produto */}
        {showAddProduct && (
          <AddProductForm
            order={addedProducts} // Passa o array COMPLETO para o AddProductForm (se necessário para lógica interna de AddProductForm)
            setOrder={setAddedProducts} // Passa o setter para o AddProductForm (se necessário para lógica interna de AddProductForm)
            product={undefined} // Nenhum produto específico para edição (modo de adição)
            onClose={() => setShowAddProduct(false)} // Fecha o formulário de adição
            onRemoveProduct={handleRemoveProduct} // Passa a função de remoção (se usada para novos produtos)
            onProductUpdated={handleUpdateProduct} // Pode ser passado para consistência, mas não será usado para adição
            isEditing={false} // Indica que NÃO está em modo de edição
            onProductAdded={handleNewProductAdded} // Passa a nova prop para adição
          />
        )}
        <AddButton onClick={() => {
          setShowAddProduct(!showAddProduct);
          setOpenProductIndex(null); // Fecha qualquer formulário de edição se estiver aberto
        }}>
          <AddButtonImg src={plus} alt="Adicionar" />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  );
};

export default EditFashionOrder;
